import { clsx, type ClassValue } from "clsx"
import { twMerge } from "tailwind-merge"

import { format, isToday, isYesterday, differenceInCalendarDays } from "date-fns"
import { sv } from "date-fns/locale"
import { ChartRow } from "@/types/ui"

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs))
}

// Keep only unique elements in array
export function uniqueOnly<E>(arr: E[]) {
  return Array.from(new Set<E>(arr)) as E[];
}

// Remove all empty elements from the array (I.e elements that match Boolean -> false)
export function removeEmpty<E>(arr: E[]) {
  return arr.filter(Boolean);
}

// Ensure the provided value is a string, and that it has a searchable format (no spaces and lower-case)
export function toSearchString<E>(item: E) {
  return item && typeof item === "string" ? item.trim().toLowerCase() : "";
}

// True when the provided search string is either empty (not specified) or equal to the target string
export function searchStringMatches(searchString: string, targetString: string) {
  return !searchString || toSearchString(searchString) === toSearchString(targetString);
}





// Formats a date as a relative Swedish date string, e.g., "Idag", "Igår", "3 dagar sedan", or a formatted date.
export function relativeSwedishDate(
  date: Date,
  opts?: { maxDays?: number; fallbackFormat?: string }
) {
  const maxDays = opts?.maxDays ?? 7
  const fallbackFormat = opts?.fallbackFormat ?? "yyyy-MM-dd"

  if (isToday(date)) return "Idag"
  if (isYesterday(date)) return "Igår"

  const days = differenceInCalendarDays(new Date(), date) // calendar-days, not hours

  if (days > 0 && days <= maxDays) {
    return `${days} dagar sedan`
  }

  // If it's in the future or older than maxDays, show a date (or "Imorgon" if you want)
  return format(date, fallbackFormat, { locale: sv })
}

export function lowestCommonRoute(knownRoutes: string[], currentUrl: string): string {
  // Normalize (remove trailing slashes except root)
  const normalize = (p: string) =>
    p === "/" ? "/" : p.replace(/\/+$/, "");

  const current = normalize(currentUrl);

  let bestMatch = "/";

  for (const route of knownRoutes) {
    const r = normalize(route);

    // Must match whole path segment (avoid /home matching /home2)
    if (
      current === r ||
      current.startsWith(r + "/")
    ) {
      // Pick the longest valid match
      if (r.length > bestMatch.length) {
        bestMatch = r;
      }
    }
  }

  return bestMatch;
}

export function checkSameRoute(a: string, b: string): boolean {
  const normalize = (url: string) => {
    // Remove query string & hash
    const clean = url.split(/[?#]/)[0];

    // Remove trailing slashes except root
    return clean === "/" ? "/" : clean.replace(/\/+$/, "");
  };

  return normalize(a) === normalize(b);
}


export function normalizeRoute(url: string): string {
  // remove query/hash, trim trailing slash (except "/")
  const clean = url.split(/[?#]/)[0];
  return clean === "/" ? "/" : clean.replace(/\/+$/, "");
}

function parseISODateUTC(dateString: string) {
  const [year, month, day] = dateString.split("-").map(Number);
  return new Date(Date.UTC(year, month - 1, day));
}

export function groupComparisonDataByMonth(rows: ChartRow[]): ChartRow[] {
  const monthMap = new Map<
    string,
    { thisYear: number; lastYear: number; hasThisYear: boolean; hasLastYear: boolean }
  >();

  for (const row of rows) {
    const date = parseISODateUTC(row.date);
    const monthDate = `${date.getUTCFullYear()}-${String(
      date.getUTCMonth() + 1
    ).padStart(2, "0")}-01`;

    if (!monthMap.has(monthDate)) {
      monthMap.set(monthDate, {
        thisYear: 0,
        lastYear: 0,
        hasThisYear: false,
        hasLastYear: false,
      });
    }

    const month = monthMap.get(monthDate)!;

    if (row.thisYear != null) {
      month.thisYear += row.thisYear;
      month.hasThisYear = true;
    }

    if (row.lastYear != null) {
      month.lastYear += row.lastYear;
      month.hasLastYear = true;
    }
  }

  return Array.from(monthMap.entries()).map(([date, values]) => ({
    date,
    thisYear: values.hasThisYear ? values.thisYear : null,
    lastYear: values.hasLastYear ? values.lastYear : null,
  }));
}